import React from "react";
import HourlyChart from "./HourlyChart";
import { ago, Dash, Empty, fmt, fmt2, hrs, Num, pct, Tag } from "./ui";

function KpiCards({ s }) {
  const cards = [
    { k: "Sent to SQS", v: s.sqs_sent, sub: "messages from Amazon", c: s.sqs_errors ? "red" : "" },
    {
      k: "Picked up", v: s.sqs_picked_up, sub: s.sqs_waiting ? `${fmt(s.sqs_waiting)} still in the queues` : "queues are empty",
      c: s.sqs_waiting > 1000 ? "amber" : ""
    },
    {
      k: "Raw records saved", v: s.raw_received, sub: s.unmapped ? `${fmt(s.unmapped)} not mapped to a store` : "all mapped to a store",
      c: s.unmapped ? "red" : ""
    },
    { k: "In the hourly table", v: s.converted, sub: `${pct(s.converted, s.raw_received)} of the day's records`, c: "ok" },
    {
      k: "Waiting for rebuild", v: s.pending, sub: `${s.stores_behind} of ${s.stores_total} stores behind`,
      c: s.stores_error ? "red" : s.pending ? "amber" : "ok"
    },
    {
      k: "Hourly rows in warehouse", v: s.hourly_in_dw,
      sub: `${pct(s.hourly_in_dw, s.hourly_rows)} of ${fmt(s.hourly_rows)}, last sync ${ago(s.dw_last_sync_at)}`,
      c: s.dw_level === "ok" ? "ok" : s.dw_level
    },
  ];
  return (
    <div className="ams-cards">
      {cards.map((c) => (
        <div className="ams-card" key={c.k}>
          <div className="ams-card-k">{c.k}</div>
          <div className={`ams-card-v${c.c ? ` ams-card-v--${c.c}` : ""}`}>{fmt(c.v)}</div>
          <div className="ams-card-s">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function DayFacts({ s, dispatch }) {
  const og = s.raw_orders - s.hourly_orders;
  const sg = s.raw_sales - s.hourly_sales;
  const rows = [
    ["Orders in raw records", fmt2(s.raw_orders)],
    ["Orders in hourly table", fmt2(s.hourly_orders)],
    ["Orders missing", fmt2(og), Math.abs(og) > 0.001 ? "amber" : ""],
    ["Sales in raw records", fmt2(s.raw_sales)],
    ["Sales in hourly table", fmt2(s.hourly_sales)],
    ["Sales missing", fmt2(sg), Math.abs(sg) > 0.01 ? "amber" : ""],
    ["Stores receiving data", `${s.stores_receiving} of ${s.stores_total}`],
    ["Rebuild runs", dispatch.available ? `${fmt(dispatch.runs)}${dispatch.failed ? ` (${dispatch.failed} failed)` : ""}` : "no task results"],
    ["Stores needing dev work", String(s.stores_red), s.stores_red ? "red" : ""],
  ];
  return (
    <dl className="ams-facts">
      {rows.map(([k, v, c]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd className={c ? `ams-facts--${c}` : ""}>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

const WAREHOUSE_TAG = { red: ["red", "Stuck"], amber: ["amber", "Slow"], ok: ["ok", "Up to date"] };

function WarehouseTable({ rows }) {
  return (
    <table className="ams-table">
      <thead>
        <tr>
          <th>Table</th><th>Last sync</th><th className="ams-num">Waiting now</th><th className="ams-num">Oldest waiting</th>
          <th className="ams-num">This day</th><th>Alert</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((w) => {
          const [lvl, text] = WAREHOUSE_TAG[w.level];
          return (
            <tr key={w.table} className={`ams-edge--${w.level}`}>
              <td className="ams-name">{w.table}</td>
              <td>{w.last_sync_at ? ago(w.last_sync_at) : "never"}</td>
              <td className="ams-num"><Num value={w.waiting} /></td>
              <td className={`ams-num${w.level === "red" ? " ams-bad" : w.level === "amber" ? " ams-warn" : ""}`}>
                {w.oldest_waiting_h == null ? <Dash /> : hrs(w.oldest_waiting_h)}
              </td>
              <td className="ams-num">
                {w.today ? <>{pct(w.today.in_dw, w.today.total)} <span className="ams-dash">of {fmt(w.today.total)}</span></> : <Dash />}
              </td>
              <td><Tag level={lvl}>{text}</Tag></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SqsTable({ rows }) {
  return (
    <table className="ams-table">
      <thead>
        <tr>
          <th>Queue</th><th>Dataset</th><th>Region</th><th className="ams-num">Sent</th><th className="ams-num">Picked up</th>
          <th className="ams-num">Waiting now</th><th className="ams-num">In flight</th><th className="ams-num">Oldest message</th><th>Problem</th>
        </tr>
      </thead>
      <tbody>
        {rows.length ? rows.map((q) => (
          <tr key={q.queue} className={`ams-edge--${q.level}`}>
            <td className="ams-name">{q.queue}</td>
            <td>{q.dataset}</td>
            <td>{q.region}</td>
            <td className="ams-num"><Num value={q.sent} /></td>
            <td className="ams-num"><Num value={q.picked_up} /></td>
            <td className="ams-num"><Num value={q.waiting} /></td>
            <td className="ams-num"><Num value={q.in_flight} /></td>
            <td className="ams-num">{q.oldest_age_min == null ? <Dash /> : `${q.oldest_age_min} min`}</td>
            <td className="ams-why ams-why--red">{q.error || ""}</td>
          </tr>
        )) : <Empty cols={9}>No queues are configured. Add them to MARKETING_STREAM_QUEUES.</Empty>}
      </tbody>
    </table>
  );
}

function DatasetsTable({ rows }) {
  return (
    <table className="ams-table">
      <thead>
        <tr>
          <th>Dataset</th><th className="ams-num">Received</th><th className="ams-num">In hourly</th>
          <th className="ams-num">Waiting</th><th className="ams-num">In warehouse</th>
          <th className="ams-num">Unmapped</th><th className="ams-num">Stores</th>
        </tr>
      </thead>
      <tbody>
        {rows.length ? rows.map((x) => (
          <tr key={x.dataset}>
            <td className="ams-name">{x.dataset}</td>
            <td className="ams-num">{fmt(x.received)}</td>
            <td className="ams-num">{fmt(x.converted)}</td>
            <td className={`ams-num${x.pending ? " ams-warn" : ""}`}>{fmt(x.pending)}</td>
            <td className="ams-num">{fmt(x.in_dw)} <span className="ams-dash">{pct(x.in_dw, x.received)}</span></td>
            <td className={`ams-num${x.unmapped ? " ams-bad" : ""}`}>{fmt(x.unmapped)}</td>
            <td className="ams-num">{fmt(x.stores)}</td>
          </tr>
        )) : <Empty cols={7}>No raw records arrived on this day. Check the SQS queues above.</Empty>}
      </tbody>
    </table>
  );
}

function Panel({ title, note, children, flush }) {
  return (
    <section className={`ams-panel${flush ? " ams-panel--flush" : ""}`}>
      <header className="ams-panel-head">
        <h2>{title}</h2>
        {note && <span className="ams-panel-note">{note}</span>}
      </header>
      {children}
    </section>
  );
}

export default function DataTab({ overview }) {
  const s = overview.summary;
  return (
    <>
      <KpiCards s={s} />
      <div className="ams-grid ams-grid--chart">
        <Panel title="Raw records received, by hour" note="UTC, when records reached our database">
          <HourlyChart data={overview.hourly_received} />
        </Panel>
        <Panel title="Is the day complete?" note="Raw records vs hourly table">
          <DayFacts s={s} dispatch={overview.dispatch} />
        </Panel>
      </div>
      <div className="ams-grid ams-grid--two">
        <Panel title="Warehouse sync" note="Waiting now, and each table's last sync" flush>
          <div className="ams-tablewrap"><WarehouseTable rows={overview.warehouse} /></div>
        </Panel>
        <Panel title="Datasets" note="Raw records received on this day" flush>
          <div className="ams-tablewrap"><DatasetsTable rows={overview.datasets} /></div>
        </Panel>
      </div>
      <Panel title="SQS queues" flush
        note="Sent = messages Amazon put in the queue. Picked up = saved and deleted by our fill job. One message can carry several records.">
        <div className="ams-tablewrap"><SqsTable rows={overview.sqs} /></div>
      </Panel>
    </>
  );
}
